/* Schedule, venues, credits and flags are static JSON files, not services (D12).
   They are fetched, cached in localStorage, and served stale-then-fresh: if we
   have a copy, it goes on screen immediately and the network updates it
   underneath. Principle 3 — never show a spinner where cached content exists. */

import { useEffect, useState } from 'react';

/**
 * `men`/`women` are how the couple actually wrote the dress code, and folding
 * them into one `full` paragraph made it unscannable at exactly the moment it
 * gets read — standing in front of a suitcase. Both stay optional: an event
 * whose guidance is the same for everyone (the holi hour: "clothes you don't
 * mind ruining") uses `full` alone and renders as one line.
 */
export interface DressCode {
  short: string;
  full?: string;
  men?: string;
  women?: string;
  tags?: string[];
}

/**
 * A collaborative playlist attached to an event (D57).
 *
 * Content rather than code, like everything else in this file (D12), so the
 * couple can paste the link — or pull it — from a phone without a deploy. It
 * ships in `schedule.json` with an **empty `url`** so the shape is visible to
 * whoever fills it in, which is why `playlistOf` below treats blank as absent:
 * a half-filled key must render nothing, never a dead link.
 *
 * A link and not an embed, deliberately. Spotify's iframe is playback-only —
 * there is no add-to-playlist control inside it — so an embed would show guests
 * the playlist and give them no way to contribute, which is the entire point.
 * The link opens the app, where adding works.
 */
export interface EventPlaylist {
  url: string;
  /** The playlist's real name. Defaults to "After party playlist". */
  title?: string;
  /** The playlist's own description, which doubles as the call to action. */
  description?: string;
  /**
   * The playlist's cover art, served from `public/content/` like the wardrobe
   * PDF so it can be swapped without a deploy (D13). Square — every playlist
   * cover is — and small: it renders at 40px on Now and 64px on the event page.
   */
  coverUrl?: string;
  /** The fine print, on the event page only. Where to say it is collaborative,
   *  and what to do without Spotify. */
  note?: string;
}

export interface WeddingEvent {
  id: string;
  name: string;
  start: string;
  end?: string;
  venueId: string;
  dressCode?: DressCode;
  notes?: string;
  playlist?: EventPlaylist;
  /** Shows the reception seating plan under this event on Now while it is up
   *  next or under way (D73). A flag rather than the reception's id, so the
   *  plan can move to another event with a content edit, like the playlist. */
  seating?: boolean;
}

/**
 * The name to show when the content has not supplied one.
 *
 * Here rather than in either component, because both render it and neither is
 * the other's owner — it lived in `PlaylistCard` for one commit and
 * `EventDetail` immediately re-typed it as a literal, which is the whole of the
 * argument.
 */
export const PLAYLIST_FALLBACK_TITLE = 'After party playlist';

/** A playlist counts only once it has a URL — see the note above on why the
 *  key exists before the link does. */
export const playlistOf = (event: WeddingEvent): EventPlaylist | null =>
  (event.playlist?.url?.trim() ? event.playlist : null);

export interface Wardrobe { title: string; intro: string; weather?: string; pdfUrl?: string }

/**
 * Something on offer during the week that is not part of the programme — the
 * daily massages, and whatever gets added beside them.
 *
 * **Deliberately not a `WeddingEvent`.** It has a time and a venue and would
 * model perfectly well as one, and that is the trap: every screen that reads
 * `events` would pick it up, and the Schedule would list a massage between the
 * haldi and the sangeet as though a guest had to choose. These are the opposite
 * of the programme — optional, repeating, and nobody's day is built around
 * them — so they are their own list, read by `ExtraNote` and nothing else.
 *
 * `venueId` rather than a name so one spelling wins and the row can link to the
 * map, the same as an event does.
 */
export interface Extra {
  id: string;
  name: string;
  venueId: string;
  start: string;
  end?: string;
  /** Anything a guest has to do first. Printed after the venue, if present. */
  note?: string;
}

export interface Schedule {
  version: string;
  timezone: string;
  wardrobe: Wardrobe;
  events: WeddingEvent[];
  /** Absent until somebody adds one — see `Extra`. */
  extras?: Extra[];
}

export interface Venue {
  id: string; name: string; x: number; y: number; where: string;
  indoor: boolean; floor?: string;
  /** A shorter name for the pin label on the map, where space is tight.
   *  Everywhere else, including the pin's accessible name, uses `name`.
   *  Pin labels are one line, centred under the pin, with no wrapping, so a
   *  name much over ~110px (about 18 characters) on a pin near the plan's edge
   *  runs off a 320px phone. "Olive Restaurant & Beach Lawn" did; give a venue
   *  like that one of these. */
  mapLabel?: string;
}
export interface VenueDoc {
  version: string;
  resort: {
    name: string;
    planLabel: string;
    /** Which wing labels to pick out on the plan. */
    stayWings?: string[];
    /** Wing label → the OSM building ids it is made of. Content rather than
     *  code so it can be fixed without a deploy; see the note in venue.json. */
    wings?: Record<string, number[]>;
  };
  venues: Venue[];
}

/**
 * `line` is the couple's own sentence about that vendor, and it is optional
 * because a blank one is honest and an invented one is not. The card reads
 * perfectly well as name + role until they write it.
 */
export interface CreditPerson { name: string; title: string; line?: string; handle?: string; photoUrl?: string }
export interface Credits { version: string; thanks: string; groups: { role: string; people: CreditPerson[] }[] }

/**
 * One way to give something, if a guest wants to (D63).
 *
 * **`url` may legitimately be blank**, and `giftIsLive` below is the only thing
 * allowed to decide what that means. The Zola registry exists today; the UPI
 * details do not, and will arrive as an edit to `gifts.json` from a phone
 * rather than as a deploy (D12/D13). The key is in the file before the value
 * is, so that whoever fills it in can see the shape — exactly the contract
 * `playlistOf` follows for the after party playlist (D57), and for the same
 * reason: a half-filled key must render as a note, never as a dead link.
 *
 * `upiId` is the virtual payment address, shown as text a guest can copy. It is
 * separate from `url` because the two fail differently. A `upi://` link opens
 * the app chooser on Android and does nothing at all on an iPhone, where the
 * scheme is not registered unless a UPI app happens to claim it — so the link
 * is the convenience and the address typed into whichever app the guest already
 * has is the thing that actually works. Either may be present without the other.
 *
 * `image` is a path with **no extension**: the page appends `.avif`, `.webp`
 * and `.jpg` to build the `<picture>`. Three URLs that differ only by extension
 * are three chances for the content to disagree with itself, and the set is
 * produced together by `scripts/build-images.mjs` or not at all.
 */
export interface GiftOption {
  id: string;
  label: string;
  /** Who this is the easy route for. Written about banks, not nationalities. */
  audience: string;
  blurb: string;
  url: string;
  upiId?: string;
  cta?: string;
  /** Shown in place of the button while `url` and `upiId` are both blank. */
  pending?: string;
  image?: string;
}

export interface Gifts {
  version: string;
  title: string;
  intro: string;
  options: GiftOption[];
  /** The fine print, under both cards. */
  note?: string;
}

/**
 * The schemes a gift link is allowed to use.
 *
 * `upi:` is here because it is the one that makes the Indian half work at all;
 * `http:` because a pasted link often lacks the `s` and the redirect is
 * someone else's problem. Everything else is refused, and the reason is that
 * this value is hand-pasted into a JSON file from a phone and then handed
 * straight to an `href`. `javascript:` in that position runs on click, and
 * React renders it with a console warning rather than refusing it — a warning
 * nobody is reading at a wedding. The allowlist is three lines and removes the
 * question.
 */
const GIFT_SCHEMES = new Set(['https:', 'http:', 'upi:']);

/**
 * The link for a gift option, or `null` if there isn't a usable one.
 *
 * Whitespace counts as absent: a key edited to `" "` on a phone keyboard is
 * the realistic way this goes wrong, and it has to read as "not ready yet"
 * rather than put a button on screen that goes nowhere. So does a value that
 * is not a URL at all — half a pasted address is likelier here than a
 * malicious one, and both end the same way.
 */
export function giftHref(option: GiftOption): string | null {
  const raw = option.url?.trim();
  if (!raw) return null;
  try {
    return GIFT_SCHEMES.has(new URL(raw).protocol) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Is there anything here a guest can actually act on?
 *
 * Asks `giftHref` rather than looking at `url` itself, so a link that was
 * rejected takes the card to its "not ready yet" note instead of leaving it
 * nominally live with no button on it.
 */
export const giftIsLive = (option: GiftOption): boolean =>
  Boolean(giftHref(option) || option.upiId?.trim());

export interface Flags {
  READ_ONLY: boolean; readOnlyMessage: string;
  galleryEnabled: boolean; displayEnabled: boolean;
  /** Show a real GPS dot on the resort plan. Off until the venue and wing
   *  identifications in venue.json's `$tbc` are confirmed: the *position* is
   *  exact by construction, but a dot is only useful beside a building someone
   *  can name, and a precise dot next to a mislabelled building is worse than
   *  no dot at all. Optional so an older cached flags.json stays valid. */
  liveLocation?: boolean;
  /** Gates `/map?pick`, the coordinate tool. A build tool, so off — but gated
   *  by content rather than by a build flag, because it is most needed by
   *  someone standing in the resort with a phone. Optional so an older cached
   *  flags.json stays valid. */
  mapPicker?: boolean;
}

export interface Cached<T> {
  data: T | null;
  fetchedAt: number | null;
  /** True when what you are looking at came from cache and the network has not confirmed it. */
  stale: boolean;
  error: Error | null;
}

const CACHE_PREFIX = 'mv.content.';

function readCache<T>(name: string): { data: T; fetchedAt: number } | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + name);
    if (!raw) return null;
    return JSON.parse(raw) as { data: T; fetchedAt: number };
  } catch {
    return null;
  }
}

function writeCache<T>(name: string, data: T): number {
  const fetchedAt = Date.now();
  try {
    localStorage.setItem(CACHE_PREFIX + name, JSON.stringify({ data, fetchedAt }));
  } catch {
    /* A full quota is not worth an error to a guest — the fetch already worked. */
  }
  return fetchedAt;
}

export function useContent<T>(name: string): Cached<T> {
  const [state, setState] = useState<Cached<T>>(() => {
    const cached = readCache<T>(name);
    return cached
      ? { data: cached.data, fetchedAt: cached.fetchedAt, stale: true, error: null }
      : { data: null, fetchedAt: null, stale: false, error: null };
  });

  useEffect(() => {
    let live = true;
    fetch(`/content/${name}.json`, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`${name}: ${r.status}`);
        return r.json() as Promise<T>;
      })
      .then((data) => {
        if (!live) return;
        const fetchedAt = writeCache(name, data);
        setState({ data, fetchedAt, stale: false, error: null });
      })
      .catch((error: Error) => {
        if (!live) return;
        // Offline with a cache is a state, not an error (principle 4). Keep what
        // is on screen and let the offline pill explain the timestamp.
        setState((prev) => (prev.data ? { ...prev, stale: true } : { ...prev, error }));
      });
    return () => { live = false; };
  }, [name]);

  return state;
}

export const useSchedule = () => useContent<Schedule>('schedule');
export const useVenues = () => useContent<VenueDoc>('venue');
export const useCredits = () => useContent<Credits>('credits');
export const useFlags = () => useContent<Flags>('flags');
export const useGifts = () => useContent<Gifts>('gifts');
